/**
 * IR — reprezentacja pośrednia wydruku.
 *
 * `build()` każdego wzoru zwraca listę WĘZŁÓW pozycjonowanych w MILIMETRACH
 * (nigdy w dotach — przelicznik na doty zna wyłącznie enkoder, bo tylko on
 * wie, czy drukarka ma 203 czy 300 dpi). Jedna definicja logiczna wychodzi
 * potem jako ZPL na Zebrze, EPL/TSPL na XPrinterze, ESC/POS na kasowej,
 * facsimile HTML w podglądzie i PDF na sterowniku systemowym.
 *
 * PackFlow dowodzi, po co to jest: etykieta GoodSpeed istniała tam jako
 * ręcznie sklejany EPL ORAZ szablon Liquid→Puppeteer i obie wersje się
 * rozjechały (pole „Region" puste na PDF, poprawne na EPL). Z IR taki rozjazd
 * jest niezapisywalny — treść jest jedna, różnią się tylko enkodery.
 */

import { MARGIN_MM } from './doc/tokens';
import { UnknownPrintMediaError } from './errors';
import type { PrintMedia } from './types';

export interface TextNode {
  kind: 'text';
  xMm: number;
  yMm: number;
  text: string;
  /** Rozmiar w punktach typograficznych (domyślnie 8 pt). */
  sizePt?: number;
  bold?: boolean;
  /**
   * Krój. Honorowany WYŁĄCZNIE na torze HTML/PDF — drukarki termiczne mają
   * fonty urządzenia (ZPL `^A0`, bitmapowe A–H w EPL/TSPL) i krój wybiera
   * w nich rozmiar, nie projektant. Dlatego walidator układu w ogóle nie
   * wpuszcza `kroj` do wzoru z celem termicznym: podgląd pokazujący szeryfy
   * i naklejka wychodząca bezszeryfowo to ta sama klasa wady, przed którą
   * broni cała ta architektura.
   */
  font?: 'ui' | 'serif' | 'mono';
  /**
   * Szerokość bloku tekstu. Enkodery termiczne łamią/przycinają do niej,
   * HTML/PDF ustawia `width` — bez tego długa nazwa receptury wyjeżdża
   * poza etykietę zamiast się zawinąć.
   */
  maxWidthMm?: number;
  /** Wyrównanie w bloku `maxWidthMm` (bez bloku ignorowane). */
  align?: 'left' | 'center' | 'right';
}

export interface BarcodeNode {
  kind: 'barcode';
  xMm: number;
  yMm: number;
  data: string;
  /** Zamknięta lista symbolik v1 — charsetu pilnuje enkoder. */
  symbology: 'code128' | 'ean13';
  heightMm: number;
  /** Czy drukować wartość pod kodem (human readable). */
  showText?: boolean;
}

export interface QrNode {
  kind: 'qr';
  xMm: number;
  yMm: number;
  data: string;
  sizeMm: number;
}

/**
 * Linia ORTOGONALNA (pozioma albo pionowa). Języki termiczne rysują linie
 * jako czarne prostokąty — ukośnych nie ma, więc IR ich nie obiecuje.
 */
export interface LineNode {
  kind: 'line';
  xMm: number;
  yMm: number;
  x2Mm: number;
  y2Mm: number;
  thicknessMm?: number;
}

export interface BoxNode {
  kind: 'box';
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  thicknessMm?: number;
}

/**
 * Obraz (logo tenanta, piktogram). W v1 rasteryzację na języki termiczne
 * (`^GFA` itd.) robi pipeline chmurowy print-core (plan §5.2), NIE enkodery —
 * enkodery termiczne rysują w miejscu obrazu ramkę-placeholder, żeby układ
 * etykiety się nie rozjechał. HTML/PDF renderuje `dataUrl` normalnie.
 */
export interface ImageNode {
  kind: 'image';
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  /** Obraz osadzony (`data:image/png;base64,...`) — nigdy zdalny URL. */
  dataUrl?: string;
  alt?: string;
}

/**
 * Powtórzenie wierszy (lista składników, pozycje dokumentu): każdy wiersz to
 * lista węzłów pozycjonowanych WZGLĘDEM początku wiersza; enkoder przesuwa
 * wiersz `i` o `yMm + i * stepMm`.
 */
export interface RepeatNode {
  kind: 'repeat';
  yMm: number;
  stepMm: number;
  rows: PrintNode[][];
  /**
   * INTENCJA PROJEKTOWA łamania, gdy wiersze powstały z `zawijaj()`.
   *
   * `rows` niesie WYNIK łamania dla jednego zestawu zmiennych, a nie regułę.
   * Odzyskiwanie `maxChars`/`maxLines` z samego wyniku jest niewykonalne:
   * najmniejsza para, która odtwarza podgląd, jest z definicji ≤ parze
   * zadeklarowanej, więc zasiew konsekwentnie schodził poniżej projektu
   * (alergeny 29/2 zamiast 38/3, waste_type 28/1 zamiast 30/2). Skutek
   * widać dopiero na DANYCH TENANTA — dłuższych niż podgląd — jako tekst
   * ucięty wielokropkiem, którego bramka round-trip nie może zobaczyć, bo
   * liczy się wyłącznie na `previewVars`.
   *
   * Enkodery tego pola NIE czytają (rysują `rows`), więc bajty payloadu ani
   * `content_hash` się nie zmieniają. Czyta je zasiew i interpreter.
   */
  zawijanie?: { maxChars: number; maxLines: number };
}

export type PrintNode =
  | TextNode
  | BarcodeNode
  | QrNode
  | LineNode
  | BoxNode
  | ImageNode
  | RepeatNode;

/** Wynik `build()` — węzły + media, na których mają wyjść. */
export interface PrintComposition {
  media: PrintMedia;
  nodes: PrintNode[];
}

export function kompozycja(media: PrintMedia, nodes: PrintNode[]): PrintComposition {
  return { media, nodes };
}

/**
 * Zawija tekst do linii o zadanej liczbie znaków (łamanie po spacjach,
 * twarde cięcie słów dłuższych niż linia). To jedyne „łamanie" wspólne dla
 * wszystkich celów — dzieje się na poziomie IR, więc ZPL i PDF łamią W TYM
 * SAMYM miejscu i etykieta wygląda tak samo niezależnie od drukarki.
 */
export function zawijaj(text: string, maxChars: number, maxLines = 4): string[] {
  const slowa = text.trim().split(/\s+/).filter(Boolean);
  const linie: string[] = [];
  let biezaca = '';
  for (const slowo of slowa) {
    if (slowo.length > maxChars) {
      // Słowo dłuższe niż linia (numer partii, URL) — tniemy twardo.
      if (biezaca) linie.push(biezaca);
      let reszta = slowo;
      while (reszta.length > maxChars) {
        linie.push(reszta.slice(0, maxChars));
        reszta = reszta.slice(maxChars);
      }
      biezaca = reszta;
      continue;
    }
    const kandydat = biezaca ? `${biezaca} ${slowo}` : slowo;
    if (kandydat.length <= maxChars) {
      biezaca = kandydat;
    } else {
      linie.push(biezaca);
      biezaca = slowo;
    }
  }
  if (biezaca) linie.push(biezaca);
  if (linie.length > maxLines) {
    // Ucinamy z wielokropkiem zamiast wyjeżdżać poza etykietę — etykieta
    // z uciętą końcówką jest czytelna, etykieta z tekstem na kliszy nie.
    const ostatnia = linie[maxLines - 1];
    return [...linie.slice(0, maxLines - 1), `${ostatnia.slice(0, Math.max(0, maxChars - 1))}…`];
  }
  return linie;
}

/** Przelicznik mm → doty drukarki (dpi z media, domyślnie 203). */
export function mmNaDoty(mm: number, dpi: number): number {
  return Math.round((mm * dpi) / 25.4);
}

/** Rozdzielczość z media etykiety (dokumenty nie mają dpi — enkodery termiczne ich nie dotykają). */
export function dpiZMedia(media: PrintMedia): number {
  return media.kind === 'label' ? (media.dpi ?? 203) : 203;
}

/** Wymiary robocze media w mm — dla enkoderów i facsimile. */
export function wymiaryMm(media: PrintMedia): { widthMm: number; heightMm: number } {
  if (media.kind === 'label') {
    // Gałąź etykiety wychodziła PRZED `switch`, więc `default:` niżej jej nie
    // pilnował: `{kind:'label', widthMm:100}` bez wysokości dawało
    // `SIZE 100 mm,undefined mm` w TSPL i `^LLNaN` w ZPL — czyli dokładnie ten
    // literalny „undefined" w payloadzie, przed którym broni cała ta funkcja.
    if (
      !Number.isFinite(media.widthMm) ||
      !Number.isFinite(media.heightMm) ||
      media.widthMm <= 0 ||
      media.heightMm <= 0
    ) {
      throw new UnknownPrintMediaError(media);
    }
    return { widthMm: media.widthMm, heightMm: media.heightMm };
  }
  switch (media.page) {
    case 'A4':
      return { widthMm: 210, heightMm: 297 };
    case 'A5':
      return { widthMm: 148, heightMm: 210 };
    case 'receipt80mm':
      // Paragon jest ciągły — wysokość to sensowny domyślny kadr podglądu.
      return { widthMm: 80, heightMm: 200 };
    default:
      // Media z JSON-a (układ tenanta, payload REST) mogą mieć `page` spoza
      // słownika. Bez tej gałęzi funkcja zwracała `undefined`, a wołający
      // destrukturyzował je na `TypeError: Cannot destructure` — błąd bez
      // kodu, którego print-core nie umiał zmapować na HTTP.
      throw new UnknownPrintMediaError(media);
  }
}

/**
 * Margines strony w mm dla media — JEDYNE źródło prawdy o marginesie.
 *
 * Czytają go dwa miejsca, które MUSZĄ się zgadzać: `@page { margin }`
 * w `doc/layout.ts` i rozmiar kontenera we `fragmentHtml`. Gdy się rozjadą,
 * Chromium nie zgłasza błędu — po cichu SKALUJE całą stronę (patrz
 * `obszarZadrukuMm`), więc rozjazd jest niewidoczny aż do linijki na papierze.
 *
 * Etykieta nie ma marginesu: jej `@page` to dokładnie wymiary etykiety,
 * a odsunięcie od krawędzi jest sprawą wzoru, nie strony.
 */
export function marginesStronyMm(media: PrintMedia): number {
  if (media.kind === 'label') return 0;
  const margines = MARGIN_MM[media.page];
  // `rozmiarStrony()` woła tę funkcję PRZED `wymiaryMm`, więc bez własnego
  // sprawdzenia nieznana strona wracała jako `undefined` i ścieżka dokumentowa
  // ginęła na `Cannot destructure property 'size'` — gołym `TypeError`,
  // mimo że `wymiaryMm` miało już typowany błąd.
  if (typeof margines !== 'number' || !Number.isFinite(margines)) {
    throw new UnknownPrintMediaError(media);
  }
  return margines;
}

/**
 * Obszar zadruku — wymiary media POMNIEJSZONE o marginesy strony.
 *
 * To, a nie `wymiaryMm`, jest rozmiarem kontenera treści. Powód zmierzony
 * prawdziwym Chromium (`preferCSSPageSize: true`, A4, margines 12 mm):
 * kontener szerszy niż obszar zadruku nie przelewa się na drugą stronę i nie
 * zostaje przycięty — Chromium DOPASOWUJE go do szerokości, skalując CAŁĄ
 * stronę współczynnikiem `obszar / kontener`. Przy kontenerze 210 mm
 * w obszarze 186 mm daje to 0.89: ramka zadeklarowana we wzorze jako
 * 182 × 269 mm wychodziła na papier jako 161 × 238 mm, a żaden test tego nie
 * widział, bo liczba stron pozostawała równa jeden.
 */
export function obszarZadrukuMm(media: PrintMedia): { widthMm: number; heightMm: number } {
  const { widthMm, heightMm } = wymiaryMm(media);
  const margines = marginesStronyMm(media);
  return { widthMm: widthMm - 2 * margines, heightMm: heightMm - 2 * margines };
}
